import type {
  CreateProjectWorkItemInput,
  ProjectWorkItem,
  UpdateProjectWorkItemInput,
} from "@pericles/framework-frontend";

interface ListResponse {
  ok: boolean;
  items?: ProjectWorkItem[];
  error?: string;
}

interface MutateResponse {
  ok: boolean;
  item?: ProjectWorkItem;
  error?: string;
}

function handleError(response: Response, payload: { error?: string }, fallback: string): never {
  const message = payload?.error ?? `${fallback} (status ${response.status})`;
  throw new Error(message);
}

export async function fetchProjectWorkItems(projectId: string): Promise<ProjectWorkItem[]> {
  const response = await fetch(`/api/projects/${projectId}/work-items`);
  const payload = (await response.json()) as ListResponse;

  if (!response.ok || payload.ok === false) {
    handleError(response, payload, "Failed to fetch project work items");
  }

  return payload.items ?? [];
}

export async function createProjectWorkItem(
  projectId: string,
  input: CreateProjectWorkItemInput,
): Promise<ProjectWorkItem> {
  const response = await fetch(`/api/projects/${projectId}/work-items`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = (await response.json()) as MutateResponse;

  if (!response.ok || payload.ok === false || !payload.item) {
    handleError(response, payload, "Failed to create work item");
  }

  return payload.item;
}

export async function updateProjectWorkItem(
  projectId: string,
  id: string,
  input: UpdateProjectWorkItemInput,
): Promise<ProjectWorkItem> {
  const response = await fetch(`/api/projects/${projectId}/work-items/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  const payload = (await response.json()) as MutateResponse;

  if (!response.ok || payload.ok === false || !payload.item) {
    handleError(response, payload, "Failed to update work item");
  }

  return payload.item;
}
